import React from 'react';
import {
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import BusinessAreaSelector from '../../common/BusinessAreaSelector';
import ClusterSelector from '../../common/ClusterSelector';
import ClientSelector from '../../common/ClientSelector';
import ClientPartyTypeSelector from '../../common/ClientPartyTypeSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import EmployeeSearch from '../../common/EmployeeSearch';
import ProjectStatusSelector from '../../common/ProjectStatusSelector';
import ProjectScopeTypeSelector from '../../common/ProjectScopeTypeSelector';

const ProjectsRepositoryFilters = ({
  searchInputValue = '',
  setSearchInputValue,
  selectedBusinessAreas = [],
  setSelectedBusinessAreas,
  selectedClients = [],
  setSelectedClients,
  selectedBusinessUnits = [],
  setSelectedBusinessUnits,
  selectedProjectStatus = ['active'],
  setSelectedProjectStatus,
  selectedCluster = '',
  setSelectedCluster,
  selectedClientPartyTypes = ['External'],
  setSelectedClientPartyTypes,
  selectedScopeTypes = [],
  setSelectedScopeTypes,
  selectedManager = null,
  setSelectedManager,
  includeHierarchyProjects = false,
  setIncludeHierarchyProjects,
  isLoadingAccessibleProjects = false,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {/* Row 1: Search, Project Status, Manager, Supervised */}
      <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
        <TextField
          placeholder="Search projects..."
          variant="outlined"
          size="small"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
            }
          }}
          sx={{
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              height: '40px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              }
            },
            '& .MuiInputBase-input': {
              padding: '8px 14px',
            }
          }}
        />

        <ProjectStatusSelector
          multiple
          value={selectedProjectStatus}
          onChange={setSelectedProjectStatus}
          label="Project Status"
          size="small"
          showEmptyOption={false}
          fullWidth={false}
          sx={{ width: 200 }}
        />

        <EmployeeSearch
          value={selectedManager}
          onChange={setSelectedManager}
          label="Filter by Representative"
          placeholder="Search representative..."
          width={300}
          height="40px"
        />

        <Tooltip title={!selectedManager ? "Select a representative first" : ""}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeHierarchyProjects}
                onChange={(e) => setIncludeHierarchyProjects(e.target.checked)}
                disabled={!selectedManager}
                size="small"
              />
            }
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                Supervised projects
                {isLoadingAccessibleProjects && <CircularProgress size={14} />}
              </span>
            }
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                color: !selectedManager ? '#9CA3AF' : '#374151',
              }
            }}
          />
        </Tooltip>
      </div>

      {/* Row 2: Clusters, Business Units, Business Areas, Clients, Client Party Type, Project Scope */}
      <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
        <ClusterSelector
          value={selectedCluster}
          onChange={setSelectedCluster}
          label="Clusters"
          placeholder="All Clusters"
          allowSelectAll={true}
          allOptionLabel="All Clusters"
          allOptionValue=""
          leafOnly={true}
          showStructureType // ADR-119
          size="small"
          sx={{ minWidth: 200 }}
        />

        <BusinessUnitSelector
          multiple
          value={selectedBusinessUnits}
          onChange={setSelectedBusinessUnits}
          label="Business Units"
          clusterId={selectedCluster}
          showStructureType
          size="small"
          sx={{ minWidth: 200 }}
        />

        <BusinessAreaSelector
          multiple
          value={selectedBusinessAreas}
          onChange={setSelectedBusinessAreas}
          label="Filter by Business Areas"
          size="small"
          sx={{ minWidth: 200 }}
        />

        <ClientSelector
          multiple
          value={selectedClients}
          onChange={setSelectedClients}
          label="Filter by Clients"
          size="small"
          enableClientCreation={false}
          sx={{ minWidth: 200 }}
        />

        <ClientPartyTypeSelector
          multiple
          value={selectedClientPartyTypes}
          onChange={setSelectedClientPartyTypes}
          label="Client Party Type"
          size="small"
          showEmptyOption={false}
          sx={{ minWidth: 200 }}
        />

        <ProjectScopeTypeSelector
          multiple
          value={selectedScopeTypes}
          onChange={setSelectedScopeTypes}
          label="Project Scope"
          size="small"
          showEmptyOption={false}
          sx={{ minWidth: 200 }}
        />
      </div>
    </div>
  );
};

export default ProjectsRepositoryFilters;
